import { useStore } from '../store';
import { Loader2, CheckCircle2, XCircle, Circle, X, BookOpen, Sparkles } from 'lucide-react';
import type { Section } from '../types';

interface ChainProgressBarProps {
  sections: Section[];
  onCancel: () => void;
}

export default function ChainProgressBar({ sections, onCancel }: ChainProgressBarProps) {
  const { chainProgress, isChainGenerating, storyOutline } = useStore();

  if (!isChainGenerating || !chainProgress) return null;

  const totalSections = chainProgress.totalSections || sections.length;
  const currentIndex = chainProgress.currentIndex ?? -1;
  const isPlanning = chainProgress.type === 'planning';
  const isPlanningComplete = chainProgress.type === 'planning_complete' || storyOutline !== null;

  // Calculate completed count
  let completedCount = 0;
  if (chainProgress.type === 'complete') {
    completedCount = totalSections;
  } else if (chainProgress.type === 'section_complete' || chainProgress.type === 'section_start') {
    completedCount = chainProgress.type === 'section_complete' ? currentIndex + 1 : currentIndex;
  }

  // Adjust progress to account for planning phase (if applicable)
  const hasPlanning = isPlanning || isPlanningComplete;
  const planningProgress = hasPlanning ? (isPlanningComplete ? 5 : 2.5) : 0;
  const sectionProgress = totalSections > 0 ? (completedCount / totalSections) * (hasPlanning ? 95 : 100) : 0;
  const progressPercent = planningProgress + sectionProgress;

  return (
    <div className="mb-4 bg-white border border-accent/25 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isPlanning ? (
            <BookOpen size={16} className="text-purple-600" />
          ) : (
            <Loader2 size={16} className="animate-spin text-accent" />
          )}
          <span className="text-sm font-medium text-gray-800">
            {isPlanning ? 'Story Mode: Planning' : 'Chain Generation'}
          </span>
          {isPlanningComplete && !isPlanning && (
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
              STORY MODE
            </span>
          )}
          <span className="text-xs text-gray-500">
            {isPlanning ? 'Creating outline...' : `${completedCount} of ${totalSections} sections`}
          </span>
        </div>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors"
          title="Cancel chain generation"
        >
          <X size={12} />
          Cancel
        </button>
      </div>

      {/* Story outline preview (when available) */}
      {storyOutline && !isPlanning && (
        <div className="mb-3 p-2 bg-purple-50 rounded-md border border-purple-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={11} className="text-purple-600" />
            <span className="text-[10px] font-medium text-purple-700 uppercase">Story Outline</span>
          </div>
          <p className="text-xs text-purple-900">{storyOutline.premise}</p>
          <div className="flex gap-2 mt-1">
            <span className="text-[10px] text-purple-600">Tone: {storyOutline.tone}</span>
            {storyOutline.themes?.length > 0 && (
              <span className="text-[10px] text-purple-600">Themes: {storyOutline.themes.slice(0, 2).join(', ')}</span>
            )}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-accent rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Per-section status indicators */}
      <div className="flex flex-wrap gap-2">
        {sections.map((section, index) => {
          let status: 'pending' | 'generating' | 'done' | 'error' = 'pending';
          if (index < completedCount) {
            status = 'done';
          } else if (index === currentIndex && chainProgress.type === 'section_start') {
            status = 'generating';
          } else if (chainProgress.type === 'section_error' && chainProgress.sectionId === section.id) {
            status = 'error';
          }

          // Get beat type for story mode display
          const plotBeat = storyOutline?.plotBeats?.find(b => b.sectionIndex === index);
          const beatLabel = plotBeat?.beatType?.replace('_', ' ');

          return (
            <div
              key={section.id}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                status === 'done'
                  ? 'bg-green-50 text-green-700'
                  : status === 'generating'
                  ? 'bg-accent/10 text-accent'
                  : status === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-gray-50 text-gray-500'
              }`}
              title={`${section.title}${beatLabel ? ` (${beatLabel})` : ''}`}
            >
              {status === 'done' && <CheckCircle2 size={11} />}
              {status === 'generating' && <Loader2 size={11} className="animate-spin" />}
              {status === 'error' && <XCircle size={11} />}
              {status === 'pending' && <Circle size={11} />}
              <span className="max-w-[100px] truncate">{section.title}</span>
              {beatLabel && status === 'generating' && (
                <span className="text-[9px] text-accent/70 capitalize">({beatLabel})</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
